import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReservationService } from '../../services/reservation.service';
import { GetRoomReservationResponse } from '../../core/types/room-reservation-response.type';
import { ActivatedRoute, Router } from '@angular/router';
import { Guest } from '../../core/types/guest.type';
import { PrivacyItem, PrivacyItemOption, PrivacyItemOptionToPass } from '../../core/types/privacy-item.type';
import { LoaderComponent } from '../../components/loader/loader.component';
import { ConfigResponse } from '../../core/types/config-response.type';
import {
  FormGroup,
  FormBuilder,
  ReactiveFormsModule,
  AbstractControl,
  Validators,
  ValidatorFn,
  FormControl,
  FormArray,
  ValidationErrors,
} from '@angular/forms';
import { InfoConfirmationBoxComponent } from '../../components/info-confirmation-box/info-confirmation-box.component';
import { DataResumenComponent } from '../../components/data-resumen/data-resumen.component';
import { Subscription, lastValueFrom } from 'rxjs';
import { UploadService } from '../../services/upload.service';
import { sanitizeString, translateLangCode } from '../../utils';
import { ConfigurationService } from '../../services/configuration.service';
import { TranslateModule } from '@ngx-translate/core';
import { PdfComponentComponent } from '../../components/pdf-component/pdf-component.component';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { StorageService } from '../../services/storage.service';
import { RoomReservation } from '../../core/types/room-reservation.type';
import { GuestItem } from '../../core/types/guest-item.type';
import { PrivacyItemType } from '../../core/types/privacy-item-type.type';

@Component({
  selector: 'app-result-page',
  templateUrl: './result-page.component.html',
  styleUrls: ['./result-page.component.css'],
  imports: [
    InfoConfirmationBoxComponent,
    DataResumenComponent,
    LoaderComponent,
    ReactiveFormsModule,
    TranslateModule,
    PdfComponentComponent,
    CommonModule,
    FontAwesomeModule,
  ],
  standalone: true,
})
export class ResultPageComponent implements OnInit, OnDestroy {
  @Input() propertyCode!: string;
  @Input() firstName!: string;
  @Input() lastName!: string;
  @Input() verificationCode!: string;
  @Input() langCode: string = 'it_IT';

  private subscription = new Subscription();
  propertyId!: number | undefined;
  private isFirstDraw = true;
  private lastX!: number;
  private lastY!: number;
  private minPlateLength = 5;
  private minTelephoneLength = 7;
  //SIGNATURE
  @ViewChild('signatureCanvas') signatureCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('formContainer') formContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('pdfComponent') pdfComponent!: PdfComponentComponent;

  isSigning = false;
  isSigned = false;

  isUploading = false;
  canvasBorderStyle = '1px solid red';

  signature = '';
  signatureImage!: ElementRef<HTMLImageElement>;
  formValues!: unknown;
  hasFoundGuest = false;
  error!: { title: string; message: string };
  isPdfComponentVisible!: boolean;
  visiblePrivacyItems: number[] = [];
  notTextPrivacyItems: PrivacyItem[] = [];

  reservation!: GetRoomReservationResponse;
  config!: ConfigResponse | null;
  guest!: Guest;
  privacyItems: PrivacyItem[] = [];
  form!: FormGroup;
  hasResults!: boolean;
  isLoading = true;
  faTrash = faTrash;
  controlsArrayForResumen: { name: string; type: string; label: string; control: FormControl; isVisible: boolean }[] =
    [];
  guestItems: GuestItem[] = [];
  selectedPrivacyItemsValues: PrivacyItem[] = []; // Store selected privacy items values
  constructor(
    private cd: ChangeDetectorRef,
    private configurationService: ConfigurationService,
    private reservationService: ReservationService,
    private router: Router,
    private storageService: StorageService,
    private fb: FormBuilder,
    private uploadService: UploadService,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit() {
    this.subscribeToRouteChange();
    this.subscribeToConfig();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private subscribeToRouteChange() {
    this.subscription.add(
      this.route.params.subscribe((params) => {
        const { propertyCode, langCode } = params;
        if (propertyCode && langCode) {
          this.propertyCode = propertyCode;
          this.configurationService.setPropertyCode(propertyCode);
          if (translateLangCode(langCode) !== this.langCode) {
            this.langCode = langCode;
            this.configurationService.setLangCode(langCode);
            this.resetRadioButtons();
          }
        }
      })
    );
  }

  private resetRadioButtons(): void {
    // Assuming 'acceptedPrivacyItems' control holds the selected radio button values
    const privacyItemsControl = this.form.get('acceptedPrivacyItems');
    this.visiblePrivacyItems = [];
    if (privacyItemsControl) {
      this.form.reset();
      this.form.markAsPristine();
      this.form.markAsUntouched();
    }
  }

  private subscribeToConfig() {
    this.subscription.add(
      this.configurationService.config$.subscribe({
        next: (config) => {
          if (config) {
            this.config = config;
            this.guestItems = config?.guestItems ?? [];
            this.propertyId = config?.vendor.id;
            if (config?.privacyItems.length) {
              this.privacyItems = config?.privacyItems ?? [];
            }
            this.privacyItems = this.config?.privacyItems ?? [];
            this.privacyItems = this.privacyItems.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            this.notTextPrivacyItems = this.privacyItems.filter((p) => p.type !== 'text');
            this.cd.detectChanges();
            this.initReservation();
          }
        },
      })
    );
  }
  initGuestItemsControls() {
    const arrayControlsNames = ['plateCode', 'vehicleType']
    this.guestItems.filter(c=>!arrayControlsNames.includes(c.name)).forEach((guestItem: GuestItem) => {
      if (guestItem.isVisible) {
        const control = this.form.controls[guestItem.name];
        this.form.addControl(guestItem.name, control);
        const controlValue = this.controlsArrayForResumen.find((c) => c.name === guestItem.name);
        if (controlValue) {
          controlValue.control = this.convertToFormControl(control);
        } else {
          this.controlsArrayForResumen.push({
            name: guestItem.name,
            type: guestItem.type,
            label: guestItem.label,
            isVisible: guestItem.isVisible,
            control: this.convertToFormControl(control),
          });
        }
      }
    });

    // Add vehicle controls to resumen if needed
    const vehiclesArray = this.form.get('vehicles') as FormArray;
    vehiclesArray.controls.forEach((vehicleGroup: AbstractControl, i: number) => {
      this.controlsArrayForResumen.push({
        name: `plateCode_${i}`,
        type: 'text',
        label: `Targa ${i + 1}`,
        isVisible: true,
        control: (vehicleGroup as FormGroup).get('plateCode') as FormControl,
      });
      this.controlsArrayForResumen.push({
        name: `typeId_${i}`,
        type: 'select',
        label: `Tipo Veicolo ${i + 1}`,
        isVisible: true,
        control: (vehicleGroup as FormGroup).get('typeId') as FormControl,
      });
    });
  }

  private initReservation() {
    const reservation = this.setReservationFromStorage();
    if (reservation?.id) {
      this.setReservation(reservation);
      this.initForm();
      this.initGuestItemsControls();
    } else {
      this.setReservationFromService();
    }
  }

  private setReservationFromStorage(): RoomReservation | undefined {
    const reservation = this.storageService.readFromSessionStorage('reservation');
    if (reservation) {
      const reservationParsed = JSON.parse(reservation);
      if (reservationParsed.verificationCode === this.verificationCode) {
        return reservationParsed;
      }
      return;
    } else {
      return;
    }
  }

  private setReservationFromService() {
    this.subscription.add(
      this.reservationService
        .getRoomReservation(this.propertyCode, this.verificationCode, this.firstName, this.lastName)
        .subscribe({
          next: (reservation) => {
            if (reservation) {
              this.setReservation(reservation);
              this.initForm();
              this.initGuestItemsControls();
            }
          },
          error: () => {
            this.handleReservationError();
            this.cd.detectChanges();
          },
          complete: () => {
            if (!this.reservation) {
              this.handleReservationError();
              this.cd.detectChanges();
            }
          },
        })
    );
  }

  private setReservation(reservation: GetRoomReservationResponse) {
    this.reservation = reservation;
    this.reservationService.setReservation(reservation);
    this.hasResults = true;
    this.guest = reservation.guests?.filter(
      (g) =>
        sanitizeString(g.firstName) === sanitizeString(this.firstName) &&
        sanitizeString(g.lastName) === sanitizeString(this.lastName)
    )[0];
    const facilities = this.config?.vendor.facilities
      .filter((f) => this.guest.facilityIds?.includes(f.id))
      .map((f) => f.text)
      .join(', ');
    this.form.get('_facilities')?.setValue(facilities);
    this.storageService.writeToSessionStorage('reservation', JSON.stringify(reservation));
    this.isLoading = false;
    this.hasFoundGuest = !!this.guest;
  }

  private handleReservationError() {
    this.hasResults = false;
    this.error = {
      title: 'privacy_form.no_reservation_found_error_title',
      message: 'privacy_form.no_reservation_found_error_message',
    };
    this.isLoading = false;
  }

  private plateValidator = (): ValidatorFn => {
    // Regex che accetta lettere, numeri, spazi e trattini (di qualsiasi lingua) e non caratteri accentati
    const regex = /^[A-Za-z0-9 -]+$/;
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && !regex.test(value)) {
        return { lettersAndNumbersOnly: true };
      }
      return null;
    };
  }

  private minLengthValidator = (minLength: number): ValidatorFn => {
    const charsToExclude = ['-', ' ', '_', '(', ')', '+', '.'];
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && value.trim().split('').filter((char: string) => !charsToExclude.includes(char)).length < minLength) {
        return { minLength: { requiredLength: minLength, actualLength: value.length } };
      }
      return null;
    };
  }

  private phoneValidator = (): ValidatorFn => {
    const regex = /^\+?[0-9 .]{7,15}$/;

    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && !regex.test(value)) {
        return { phone: true };
      }
      return null;
    };
  }

  private initForm() {
    const allowedVehiclesCount = this.config?.vendor.allowedVehiclesCount || 1;
    const guestVehicles = this.guest?.vehicles ?? [];
    const vehiclesArray = [];
    for (let i = 0; i < Math.max(allowedVehiclesCount, guestVehicles.length); i++) {
      const vehicle = guestVehicles[i] || {};
      const plateCode = vehicle.plateCode || '';

      vehiclesArray.push(
        this.fb.group({
          plateCode: this.fb.control(
            plateCode,
            [this.plateValidator(), this.minLengthValidator(this.minPlateLength), Validators.maxLength(15)],
          ),
          typeId: this.fb.control(vehicle.typeId || 2),
          _isPlatePreloaded: this.fb.control(!!plateCode)
        })
      );
    }
    this.form = this.fb.group({
      id: this.fb.control(this.guest?.id ?? null),
      checkinNumber: this.fb.control(this.reservation?.id ?? ''),
      firstName: this.fb.control(this.guest?.firstName ?? '', Validators.required),
      lastName: this.fb.control(this.guest?.lastName ?? '', Validators.required),
      email: this.fb.control(this.guest?.email ?? null, [
        Validators.email,
        Validators.pattern('^[A-Za-z0-9._+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,4}$'),
      ]),
      verificationCode: this.fb.control(this.verificationCode, Validators.required),
      roomName: this.fb.control(this.reservation.roomName ?? ''),
      checkinDate: this.fb.control(this.guest?.checkinDate ?? ''),
      checkoutDate: this.fb.control(this.guest?.checkoutDate ?? ''),
      cityTaxAmount: this.fb.control(this.guest?.cityTaxAmount ?? 0),
      currencyCode: this.fb.control(this.guest?.currencyCode ?? 'EUR'),
      facilityIds: this.fb.control(this.guest?.facilityIds ?? []),
      adults: this.fb.control(this.reservation?.adults ?? 0),
      children: this.fb.control(this.reservation?.children ?? 0),
      vehicles: this.fb.array(vehiclesArray),
      propertyCode: this.fb.control(this.propertyCode),
      propertyName: this.fb.control(this.config?.vendor.name ?? ''),
      acceptedPrivacyItems: this.fb.control([], this.requiredPrivacyItemsValidator()),
      phone: this.fb.control(this.guest?.phone ?? '', [
        this.phoneValidator(), this.minLengthValidator(this.minTelephoneLength),
      ]),
      sendMeCopy: this.fb.control(false),
      _facilities: this.fb.control(''),
      _isSigned: this.fb.control(false, Validators.requiredTrue),
      _signatureRaw: this.fb.control(''),
      _locationCity: this.fb.control(this.config?.locationCity || this.config?.vendor.locationCity || ''),
    });
  }

  get vehiclesFormArray(): FormArray {
    return this.form.get('vehicles') as FormArray;
  }

  convertToFormControl(absCtrl: AbstractControl | null): FormControl {
    const ctrl = absCtrl as FormControl;
    return ctrl;
  }


/**
 * Custom validator that checks if all required privacy items have value "true"
 * @returns ValidatorFn
 */
requiredPrivacyItemsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // If no value or not an array, return null (let other validators handle this)
    if (!value || !Array.isArray(value)) {
      return null;
    }

    // Find all required items that don't have value "true"
    const invalidRequiredItems = value.filter((item: PrivacyItem) =>
      item.required && item.value !== 'true'
    );

    // Check privacy items with options that have required = true
    const invalidItemsWithOptionsRequired = value.filter((item: PrivacyItem) => {
      // Skip if no options
      if (!item.options || !Array.isArray(item.options)) {
        return false;
      }

      // For items with options, check if there's a selected option that is required but has value !== 'true'
      const selectedRequiredOption = item.options.find(option =>
        option.required && option.isSelected
      );

      // If there's a selected required option, it must have value 'true'
      return selectedRequiredOption && selectedRequiredOption.value !== 'true';
    });

    const allInvalidItems = [...invalidRequiredItems, ...invalidItemsWithOptionsRequired];

    // If there are invalid required items, return validation error
    if (allInvalidItems.length > 0) {
      return {
        requiredPrivacyItems: {
          message: 'All required privacy items and options must be accepted',
          invalidItems: allInvalidItems.map(item => ({
            id: item.id,
            label: item.label,
            privacyItemId: item.privacyItemId,
            hasInvalidOptions: item.options && item.options.some((option: PrivacyItemOption) =>
              option.required && (!option.isSelected || option.value !== 'true')
            )
          }))
        }
      };
    }

    // All required items are valid
    return null;
  };
}

  shouldShowItem(itemId: number) {
    return itemId === this.notTextPrivacyItems[0]?.id || this.visiblePrivacyItems.includes(itemId);
  }

  areAllBoxedClicked() {
    return this.visiblePrivacyItems.length >= this.notTextPrivacyItems.length;
  }

  onSelectOption(event: {isSelected: boolean; id: number; option: PrivacyItemOptionToPass }): void {
    const {id, option, isSelected} = event;
    if (!this.visiblePrivacyItems.includes(id)) this.visiblePrivacyItems.push(id);
    //Metto in selectedPrivacyItemsValues i privacy Items come in privacyItems pero metto il valore selezionato all'opzione

    this.privacyItems.forEach(privacyItem => {

      if (privacyItem.id == id) {
        let value = 'false';
        if (option.type === PrivacyItemType.Checkbox) {
          value = JSON.stringify(isSelected);
        } else {
          value = privacyItem.options?.find(o => o.id === option.id)?.value || '';
        }
        const existingIndex = this.selectedPrivacyItemsValues.findIndex(item => item.id == privacyItem.id);
        // If the privacy item is not found, add it with the selected value
        if (existingIndex === -1) {
          this.selectedPrivacyItemsValues.push({
            ...privacyItem,
            value, // Set the value of the privacy item
            selected: isSelected, // Set the selected state
            options: privacyItem.options?.map(o => ({
              ...o,
              isSelected: option.isSelected ? option.value === o.value : false // Update the value of the selected option
            })),
          });
        } else {
          // If it exists, update the value
          this.selectedPrivacyItemsValues[existingIndex].value = value;
          this.selectedPrivacyItemsValues[existingIndex].selected = isSelected;
          this.selectedPrivacyItemsValues[existingIndex].options = privacyItem.options?.map(o => ({
            ...o,
            isSelected: option.isSelected ? option.value === o.value : false // Update the value of the selected option
          }));
        }
      }
    });
    this.form.get('acceptedPrivacyItems')?.setValue(this.selectedPrivacyItemsValues);
    this.form.get('acceptedPrivacyItems')?.markAsDirty();
    this.form.get('acceptedPrivacyItems')?.markAsTouched();
  }

  setError() {
    this.error = {
      title: 'privacy_form.no_config_found_error_title',
      message: 'privacy_form.no_config_found_error_message',
    };
  }

  startSigning(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    this.setIsSignedFormValue(true);
    this.canvasBorderStyle = '1px solid grey';
    this.isSigning = true;

    const canvas = this.signatureCanvas.nativeElement;
    const ctx = canvas.getContext('2d');

    const rect = canvas.getBoundingClientRect();
    this.lastX = event instanceof MouseEvent ? event.clientX - rect.left : event.touches[0].clientX - rect.left;
    this.lastY = event instanceof MouseEvent ? event.clientY - rect.top : event.touches[0].clientY - rect.top;
    if (!ctx) return;
    // Remove the clearRect call
    if (this.isFirstDraw) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.isFirstDraw = false;
    }
    ctx.beginPath();
    ctx.moveTo(this.lastX, this.lastY);

    // Add focus event listener
    canvas.addEventListener('focus', () => {
      if (this.isSigning) {
        this.signing(event);
      }
    });
  }

  stopSigning(): void {
    if (!this.isSigning && this.isSigned) {
      this.canvasBorderStyle = '1px solid grey';
    } else {
      this.canvasBorderStyle = '1px solid red';
    }
    this.isSigning = false;
    this.isSigned = true;
  }

  signing(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    if (!this.isSigning) return;

    const canvas = this.signatureCanvas.nativeElement;
    const ctx = canvas.getContext('2d');

    const rect = canvas.getBoundingClientRect();
    const mouseX = event instanceof MouseEvent ? event.clientX - rect.left : event.touches[0].clientX - rect.left;
    const mouseY = event instanceof MouseEvent ? event.clientY - rect.top : event.touches[0].clientY - rect.top;
    if (!ctx) return;
    ctx.lineTo(mouseX, mouseY);
    ctx.stroke();

    this.lastX = mouseX;
    this.lastY = mouseY;
  }

  resetCanvas(): void {
    const canvas = this.signatureCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Redraw the default text
    this.isFirstDraw = true;
    this.isSigned = false;
    this.canvasBorderStyle = '1px solid red';
    this.setIsSignedFormValue(false);
  }

  createResultObjectForPdf(): unknown {
    const formValues = this.form.value;
    //Aggiungo un elemento al principio per stampare il PDF e mettere i check agli items corrispondenti
    formValues.acceptedPrivacyItems.unshift({});
    return formValues;
  }

  async onSubmit(): Promise<void> {
    await this.sendFormAndSendPDF();
  }

  private async sendFormAndSendPDF(): Promise<void> {
    const canvas = this.signatureCanvas.nativeElement;
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    this.storageService.writeToSessionStorage('signatureDataUrl', dataUrl);

    this.form.controls['_signatureRaw']?.setValue(dataUrl);
    this.formValues = this.createResultObjectForPdf();
    this.pdfComponent.formValues = this.formValues;
    this.cd.detectChanges();

    this.isUploading = true;
    let uploadUrl;
    let pdf;
    let hasPdfError = false;
    let errorObj;
    try {
      uploadUrl = await this.sendForm();
      if (!uploadUrl) throw new Error('No upload url found, Post was not successful');
    } catch (error) {
      this.isUploading = false;
      this.error = error as { title: string; message: string };
    }

    this.isPdfComponentVisible = true;

    try {
      const result = await this.pdfComponent.generatePDF();
      pdf = result.doc;
      hasPdfError = result.hasPdfError;
      errorObj = result.errorObj;
    } catch (error) {
      this.isUploading = false;
    }

    this.isPdfComponentVisible = false;
    if (uploadUrl && pdf) await lastValueFrom(this.uploadService.uploadfileAWSS3(uploadUrl, pdf));
    this.router.navigate([
      '/greetings/' + this.propertyCode + '/' + this.langCode + '/' + hasPdfError + '/' +( errorObj?.message || 'ok'),
    ]);
    this.isUploading = false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private cleanFormFromHiddenFields(data: any): void {
    // Remove hidden fields from the form data
    const dataKeys = Object.keys(data);
    dataKeys.forEach((key) => {
      if (key.startsWith('_')) {
        delete data[key];
      }
      if (data[key]) {
        if (Array.isArray(data[key])) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data[key].forEach((item: any) => {
            this.cleanFormFromHiddenFields(item);
          });
        } else if (typeof data[key] === 'object') {
          this.cleanFormFromHiddenFields(data[key]);
        }
      }
    }
    );
  }

  private cleanFormFromFunctionalFields(privacyItems: PrivacyItem[]): void {
    // Remove functional fields from the form data
    privacyItems.forEach((item: { langCode?: string; selected?: boolean; options?: { isSelected?: boolean; required?: boolean; langCode?: string }[] }) => {
      delete item.selected;
      delete item.langCode
      if (item.options) {
        item.options.forEach((option: { isSelected?: boolean; required?: boolean; langCode?: string }) => {
          delete option.isSelected;
          delete option.required;
          delete option.langCode;
        });
      }
    });
  }

  private async sendForm(): Promise<string | null> {
    const data = JSON.parse(JSON.stringify(this.form.value));
    this.cleanFormFromHiddenFields(data);
    this.cleanFormFromFunctionalFields(data.acceptedPrivacyItems);

    try {
      const response = await lastValueFrom(this.reservationService.sendResults(data));
      if (response) {
        const { uploadUrl } = response;
        return uploadUrl;
      }
      return null;
    } catch (error) {
      this.isUploading = false;
      this.error = {
        title: 'privacy_form.error_sending_form_title',
        message: 'privacy_form.error_sending_form_message',
      };
      this.cd.detectChanges();
      return null;
    }
  }

  setIsSignedFormValue(value: boolean): void {
    this.form.controls['_isSigned']?.setValue(value);
  }

  goToSearch(): void {
    const path = '/search/' + this.propertyCode + '/' + (this.langCode || 'it_IT');
    this.router.navigate([path]);
  }
}
